import { useFormik } from "formik";
import React, { useContext } from "react";
import * as Yup from "yup";

//assets
import { AppContext } from "../../context/AppContext";
import brand from "../../config/brand";
import { ReactComponent as STEAM } from "../../assets/images/Frame (22).svg";
import { ReactComponent as TWITCH } from "../../assets/images/Frame (23).svg";
import { ReactComponent as METAMASK } from "../../assets/images/Frame (24).svg";
import SocialMediaButton from "../Common/Buttons/SocialMediaButton/SocialMediaButton";
import { useAuth } from "@/context/AuthContext";
import { showErrorToast, showSuccessToast } from "@/utils/toastUtils";

const COUNTRY_OPTIONS = [
  { value: "US", label: "United States" },
  { value: "CA", label: "Canada" },
  { value: "EE", label: "Estonia" },
  { value: "SE", label: "Sweden" },
  { value: "FI", label: "Finland" },
  { value: "GB", label: "United Kingdom" },
  { value: "OTHER", label: "Other supported jurisdiction" },
];

const isAtLeast18 = (value) => {
  if (!value) return false;
  const birthDate = new Date(value);
  if (Number.isNaN(birthDate.getTime())) return false;
  const today = new Date();
  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDelta = today.getMonth() - birthDate.getMonth();
  const hasHadBirthday =
    monthDelta > 0 ||
    (monthDelta === 0 && today.getDate() >= birthDate.getDate());

  return age > 18 || (age === 18 && hasHadBirthday);
};

const Register = () => {
  const { signUp } = useAuth();
  const { updateLoggedIn } = useContext(AppContext);

  const validationSchema = Yup.object().shape({
    username: Yup.string()
      .min(3, "Username must be at least 3 characters")
      .max(24, "Username must be 24 characters or less")
      .matches(/^[a-zA-Z0-9_]+$/, "Use letters, numbers, and underscores only")
      .required("Username is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: Yup.string()
      .required("Password is required")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
        "Password must contain at least 8 characters, one lowercase letter, one uppercase letter, one number, and one special symbol"
      ),
    birthDate: Yup.string()
      .required("Date of birth is required")
      .test("age", "You must be at least 18 years old", isAtLeast18),
    country: Yup.string().required("Country is required"),
    acceptedAge: Yup.boolean().oneOf([true], "Age confirmation is required"),
    acceptedTerms: Yup.boolean().oneOf([true], "Terms acceptance is required"),
    acceptedDemo: Yup.boolean().oneOf(
      [true],
      "Demo account acknowledgement is required"
    ),
  });

  const formik = useFormik({
    initialValues: {
      username: "",
      email: "",
      password: "",
      birthDate: "",
      country: "",
      acceptedAge: false,
      acceptedTerms: false,
      acceptedDemo: false,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      try {
        await signUp(values.email, values.password, {
          name: values.username,
          birthDate: values.birthDate,
          country: values.country,
        });
        updateLoggedIn(true);
        showSuccessToast("Demo account created");
      } catch (error) {
        const message =
          error.message || "Registration failed. Please try again.";
        showErrorToast(message);
        formik.setStatus(message);
        formik.setSubmitting(false);
      }
    },
  });

  const fieldError = (name) =>
    formik.touched[name] && formik.errors[name] ? (
      <div className="error-message">{formik.errors[name]}</div>
    ) : null;

  return (
    <>
      <div>
        <form onSubmit={formik.handleSubmit}>
          <div className="demo-onboarding-note">
            <strong>Demo casino account</strong>
            <span>
              This creates a free-play {brand.casinoName} account. Real-money access would
              require identity checks, jurisdiction review, and deposit approval.
            </span>
          </div>

          <div className="form-group spacing-inputs">
            <label htmlFor="username" className="form-label">
              USERNAME <span className="required">*</span>
            </label>
            <input
              type="text"
              id="username"
              name="username"
              className="form-input"
              value={formik.values.username}
              onChange={formik.handleChange}
              placeholder="Username"
            />
            {fieldError("username")}
          </div>

          <div className="form-group spacing-inputs">
            <label htmlFor="email" className="form-label">
              EMAIL <span className="required">*</span>
            </label>
            <input
              type="text"
              id="email"
              name="email"
              className="form-input"
              value={formik.values.email}
              onChange={formik.handleChange}
              placeholder="youremail@domain.com"
            />
            {fieldError("email")}
          </div>

          <div className="form-group spacing-inputs">
            <label htmlFor="password" className="form-label">
              PASSWORD <span className="required">*</span>
            </label>
            <input
              type="password"
              id="password"
              name="password"
              className="form-input"
              value={formik.values.password}
              onChange={formik.handleChange}
              placeholder="********"
              autoComplete="new-password"
            />

            {fieldError("password")}
          </div>

          <div className="register-form-row">
            <div className="form-group spacing-inputs">
              <label htmlFor="birthDate" className="form-label">
                DATE OF BIRTH <span className="required">*</span>
              </label>
              <input
                type="date"
                id="birthDate"
                name="birthDate"
                className="form-input"
                value={formik.values.birthDate}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {fieldError("birthDate")}
            </div>

            <div className="form-group spacing-inputs">
              <label htmlFor="country" className="form-label">
                COUNTRY <span className="required">*</span>
              </label>
              <select
                id="country"
                name="country"
                className="form-input"
                value={formik.values.country}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value="">Select country</option>
                {COUNTRY_OPTIONS.map((country) => (
                  <option key={country.value} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </select>
              {fieldError("country")}
            </div>
          </div>

          <div className="demo-checklist">
            <label className="checkbox-row">
              <input
                type="checkbox"
                name="acceptedAge"
                checked={formik.values.acceptedAge}
                onChange={formik.handleChange}
              />
              <span>I confirm I am at least 18 years old.</span>
            </label>
            {fieldError("acceptedAge")}

            <label className="checkbox-row">
              <input
                type="checkbox"
                name="acceptedTerms"
                checked={formik.values.acceptedTerms}
                onChange={formik.handleChange}
              />
              <span>I accept the demo terms and responsible gaming notice.</span>
            </label>
            {fieldError("acceptedTerms")}

            <label className="checkbox-row">
              <input
                type="checkbox"
                name="acceptedDemo"
                checked={formik.values.acceptedDemo}
                onChange={formik.handleChange}
              />
              <span>
                I understand this is free-play balance and cannot be deposited or
                withdrawn.
              </span>
            </label>
            {fieldError("acceptedDemo")}
          </div>

          <div className="info-text-container">
            <p className="info-text">
              This demo flow is the front door for a casino-style account. KYC,
              sanctions checks, payment review, and limits are still roadmap
              items before real-money play.
            </p>
          </div>

          {formik.status ? (
            <div className="error-message">{formik.status}</div>
          ) : null}

          <button
            type="submit"
            className="register-button uppercase"
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? "Creating account..." : "Create demo account"}
          </button>
        </form>
      </div>

      <span
        style={{
          fontSize: "15px",
          justifyContent: "center",
          color: "#B1B6C6",
          width: "100%",
          display: "flex",
        }}
      >
        Or continue with
      </span>
      <div className="social-media-container">
        <SocialMediaButton socialIcon={STEAM} socialName={"Steam"} />
        <SocialMediaButton socialIcon={TWITCH} socialName={"Twitch"} />
        <SocialMediaButton socialIcon={METAMASK} socialName={"Metamask"} />
      </div>
    </>
  );
};

export default Register;
